import { useState } from "react";
import {
  Drawer,
  Button,
  Checkbox,
  message,
  Spin,
  Alert,
  Collapse,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_FORM_WIDTH,
} from "../../../../components";
import {
  useTenantSubFeaturesQuery,
  useUpdateTenantSubFeaturesMutation,
} from "../../../../Services";

const { Text } = Typography;

interface SubFeature {
  sub_feature_id: string;
  is_enable: number;
  sub_feature_name: string;
  sub_feature_code: string;
  feature_id: string;
  feature_name: string;
}

interface FeatureGroup {
  featureId: string;
  featureName: string;
  items: SubFeature[];
}

interface Props {
  open: boolean;
  tenantId: string;
  onClose: () => void;
}

export const TenantFeatureDrawer: React.FC<Props> = ({
  open,
  tenantId,
  onClose,
}) => {
  // Local overrides for instant UI feedback. The server is updated on every
  // toggle, so this only reflects the latest known state between the click and
  // the server's confirmation.
  const [overrides, setOverrides] = useState<Map<string, number>>(new Map());

  const {
    data: features = [],
    isLoading,
    isFetching,
    refetch,
  } = useTenantSubFeaturesQuery(open ? tenantId : undefined);
  const updateMutation = useUpdateTenantSubFeaturesMutation();

  const groups = (() => {
    const map = new Map<string, FeatureGroup>();
    for (const sf of features) {
      const g = map.get(sf.feature_id) || {
        featureId: sf.feature_id,
        featureName: sf.feature_name,
        items: [],
      };
      g.items.push(sf);
      map.set(sf.feature_id, g);
    }
    return Array.from(map.values());
  })();

  const getEffectiveEnabled = (subFeatureId: string, original: number): number =>
    overrides.has(subFeatureId) ? overrides.get(subFeatureId)! : original;

  const handleToggle = async (sf: SubFeature, checked: boolean) => {
    const newEnabled = checked ? 1 : 0;

    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(sf.sub_feature_id, newEnabled);
      return next;
    });

    try {
      const res = await updateMutation.mutateAsync({
        id: tenantId,
        sub_feature_id: sf.sub_feature_id,
        is_enable: newEnabled,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update feature.");
      }
      message.success(
        `${sf.sub_feature_name} ${checked ? "enabled" : "disabled"}.`,
      );
    } catch (err: any) {
      setOverrides((prev) => {
        const next = new Map(prev);
        next.delete(sf.sub_feature_id);
        return next;
      });
      refetch();
      message.error(err.message || "Error updating feature.");
    }
  };

  const enabledCount = features.filter((sf: SubFeature) =>
    getEffectiveEnabled(sf.sub_feature_id, sf.is_enable),
  ).length;

  const collapseItems = groups.map((g) => {
    const groupEnabled = g.items.filter((sf) =>
      getEffectiveEnabled(sf.sub_feature_id, sf.is_enable),
    ).length;
    return {
      key: g.featureId,
      label: (
        <div className="flex items-center justify-between w-full pr-2">
          <div className="flex items-center gap-2">
            <Text strong className="text-sm">
              {g.featureName}
            </Text>
            <Tag
              color={groupEnabled > 0 ? "blue" : "default"}
              bordered={false}
              className="text-[11px] leading-4"
            >
              {groupEnabled}/{g.items.length}
            </Tag>
          </div>
        </div>
      ),
      children: (
        <div className="flex flex-col gap-0.5">
          {g.items.map((sf) => {
            const effectiveEnabled = getEffectiveEnabled(
              sf.sub_feature_id,
              sf.is_enable,
            );
            return (
              <div
                key={sf.sub_feature_id}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox
                    checked={!!effectiveEnabled}
                    onChange={(e) => handleToggle(sf, e.target.checked)}
                  />
                  <Text className="text-sm truncate">
                    {sf.sub_feature_name}
                  </Text>
                  <Text
                    type="secondary"
                    className="text-[10px] hidden sm:inline"
                  >
                    {sf.sub_feature_code}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      ),
    };
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      title={drawerTitle(
        "Manage Features",
        "Enable or disable sub-features for the tenant",
      )}
      destroyOnClose
      footer={drawerFooter({
        actions: [
          {
            label: "Close",
            onClick: onClose,
          },
        ],
      })}
    >
      <div className="flex flex-col gap-4 h-full">
        {!isLoading && (
          <Alert
            type="success"
            showIcon={false}
            className="border-green-100 bg-green-50"
            message={
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-green-800 flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-500" />
                  {enabledCount}/{features.length} sub-features enabled
                </span>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                >
                  Refresh
                </Button>
              </div>
            }
          />
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading features..." size="large" />
            </div>
          )}

          {groups.length > 0 && (
            <Collapse
              defaultActiveKey={groups.map((g) => g.featureId)}
              expandIconPosition="end"
              size="small"
              ghost
              className="[&_.ant-collapse-content]:!bg-white [&_.ant-collapse-content]:!rounded-xl [&_.ant-collapse-content]:!border [&_.ant-collapse-content]:!border-gray-100"
              items={collapseItems}
            />
          )}

          {features.length === 0 && !isLoading && (
            <div className="text-center text-gray-400 text-sm py-10">
              No sub-features found.
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
